import React, { useId, useState } from "react";
import CpIcon from "../CpIcon/CpIcon";

export interface CpAccordionItem {
  title: string;
  body: React.ReactNode;
}

interface CpAccordionProps {
  items: CpAccordionItem[];
  /** Index open on first render; -1 for none. */
  defaultOpen?: number;
  style?: React.CSSProperties;
}

/** One-open-at-a-time FAQ list (CertainPath Design System `Accordion`). */
const CpAccordion: React.FC<CpAccordionProps> = ({
  items,
  defaultOpen = 0,
  style,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const baseId = useId();

  return (
    <div
      style={{
        fontFamily: "var(--font-sans)",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {items.map((item, i) => {
        const on = i === open;
        const panelId = `${baseId}-panel-${i}`;
        return (
          <div
            key={item.title}
            style={{ borderBottom: "1px solid var(--border-default)" }}
          >
            <button
              aria-controls={panelId}
              aria-expanded={on}
              onClick={() => setOpen(on ? -1 : i)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "var(--space-4)",
                padding: "var(--space-5) 0",
                border: 0,
                background: "transparent",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
                fontSize: "var(--text-h4)",
                fontWeight: 700,
                color: "var(--ink-500)",
              }}
              type="button"
            >
              <span>{item.title}</span>
              <CpIcon color="var(--red-500)" name={on ? "minus" : "plus"} />
            </button>
            {on && (
              <div
                id={panelId}
                style={{
                  padding: "0 0 var(--space-6)",
                  color: "var(--text-body)",
                  fontSize: "var(--text-body-sm)",
                  maxWidth: "var(--measure-prose)",
                }}
              >
                {item.body}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CpAccordion;
